import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable react/require-default-props */
import React, { useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import moment from 'moment';
import { useIntl } from 'react-intl';
import { formatListValue } from '@/shared/components/FormattedListValue';
import PopoverStickOnHover from '@/shared/components/PopoverStickOnHover';
import { CommentIcon, PaperclipIcon } from '@/shared/components/Icons';
import { defaultItemType, getTaskColor, Method, states, taskColors, TaskStatus, TaskType, } from '@/modules/tasks/taskboard/kanban/constants';
import authService, { functionalities, privileges } from '@/shared/services/AuthService';
import taskMessages from '@/modules/tasks/taskMessages';
import { useDataContext } from '@/shared/providers/DataProvider';
import { useGetPlannedTaskRemark } from '@/shared/hooks/task/useGetPlannedTaskRemark';
import { ModalType, useModalContext } from '@/shared/providers/ModalProvider';
import { useGetTaskAttachments } from '@/shared/hooks/task/useGetTaskAttachments';
import { useUpdateTaskStatus } from '@/shared/hooks/task/useUpdateTaskStatus';
import api from '@/shared/api';
import { minutesToTimeSpan } from '@/shared/utils/adminpulse';
import config from '@/config';
import handleHttpError from '@/shared/services/HandleHttpError';
import { useCreateTaskTimeRegistration, } from '@/shared/hooks/task/registration/useCreateTaskTimeRegistration';
import { TaskQueryKey } from '@/shared/hooks/task/queryKey';
import { useQueryClient } from '@tanstack/react-query';
import ToastrService from '@/shared/services/ToastrService';
import appMessages from '@/shared/messages/appMessages';
import EditableDatePicker from '@/shared/components/editable/EditableDatePicker';
import FormattedDateShort from '@/shared/components/FormattedDateShort';
import { useTaskTimerContext } from '@/shared/providers/TaskTimerProvider';
import { useGetTaskDocumentation } from '@/shared/hooks/task/useGetTaskDocumentation';
import dompurify from 'dompurify';
import Checkbox from '@/shared/components/input/Checkbox';
import TaskIcon from '@/modules/tasks/TaskIcon';
import TaskStatusToggle from '@/modules/tasks/TaskStatusToggle';
import RelationName from '@/shared/components/RelationName';
import { openBlankDocumentationWindow, openTaskDocumentationTab, } from '@/shared/utils/openTaskDocumentationTab';
import TaskAttachmentsPopover from '../../TaskAttachmentsPopover';
const sanitizer = dompurify.sanitize;
const stop = (e) => {
    e.stopPropagation();
};
// Slightly more compact than the react-dates default (39px): the cards themselves are narrow, so a
// full-size calendar looks oversized next to them.
const CalendarDaySize = 33;
// The calendar is a fixed size, so whether it still fits below a card can simply be measured:
// ~285px tall with the month/year selects and the Today/Cancel row, plus the input's fang.
const CalendarPopupHeight = 300;
const WeekPlannerElementComponent = ({ element, isDragging, openTaskDetailModal, onTasksUpdated, onUpdateExecutionDate, showDeleteTask = false, onDeleteTask, selectionMode = false, isSelected = false, onToggleSelected, }) => {
    var _a, _b, _c, _d, _e;
    const intl = useIntl();
    const { defaultCompany } = useDataContext();
    const { setShowModalInfo } = useModalContext();
    const { updateTaskStatus } = useUpdateTaskStatus();
    const { createTaskTimeRegistration } = useCreateTaskTimeRegistration();
    const { startStopTimer, isTimerRunningForSubTaskId, processTimeRegistrationResult, } = useTaskTimerContext();
    const queryClient = useQueryClient();
    const cardRef = useRef(null);
    // Cards low on the screen open their calendar upwards, like the deadline filter in the panel.
    const [openDatePickerUp, setOpenDatePickerUp] = useState(false);
    const [loadDocumentation, setLoadDocumentation] = useState(false);
    const [loadRemark, setLoadRemark] = useState(false);
    const [loadAttachments, setLoadAttachments] = useState(false);
    const { data: remark, isLoading: loadingRemark } = useGetPlannedTaskRemark(element.id, (_a = element.taskLevel) !== null && _a !== void 0 ? _a : 1, !!element.scheduleHasRemark, loadRemark);
    const { data: documentation, isLoading: loadingDocumentation } = useGetTaskDocumentation(element.templateId, loadDocumentation);
    const { data: attachments, isLoading: loadingAttachments } = useGetTaskAttachments(element.id, (_b = element.taskLevel) !== null && _b !== void 0 ? _b : 1, loadAttachments);
    const canUpdateTask = useMemo(() => authService.hasPermission({
        functionality: functionalities.PlannedTask,
        privilege: privileges.Update,
    }), []);
    const isTimerRunning = useMemo(() => isTimerRunningForSubTaskId === element.id, [
        element.id,
        isTimerRunningForSubTaskId,
    ]);
    const state = states[element.status];
    const openTimeRegistrationDialog = () => {
        setShowModalInfo({ modalType: ModalType.TimeRegistration, task: element });
    };
    const openMailDialog = () => {
        setShowModalInfo({
            modalType: ModalType.TaskMail,
            task: element,
            onSave: async () => {
                await updateTaskStatus({
                    id: element.id,
                    status: TaskStatus.Done,
                    isBlocked: !!element.isBlocked,
                    inapplicable: !!element.inapplicable,
                    rejected: !!element.rejected,
                });
                if (defaultCompany.taskTimeRegistrationMethod === Method.AutomaticRegistration) {
                    if (element.defaultDuration && element.defaultDuration > 0) {
                        const taskTimeRegistration = {
                            plannedTaskId: element.id,
                            plannedTaskTemplateId: element.templateId,
                            plannedTaskName: element.name,
                            invoiceStreamId: element.invoiceStreamId,
                            relationId: element.relationId,
                            resourceId: element.resourceId,
                            durationMinutes: String(element.defaultDuration),
                            mainPlannedTaskId: element.parentTaskId,
                            mainTaskTemplateId: element.parentTemplateId,
                            mainPlannedTaskName: element.parentName,
                        };
                        const timeRegistrationResult = await createTaskTimeRegistration(taskTimeRegistration);
                        await processTimeRegistrationResult(timeRegistrationResult);
                    }
                    else {
                        ToastrService.info(intl.formatMessage(appMessages.msgNoRegistrationMadeDefaultTimeZero));
                    }
                }
                else if (defaultCompany.taskTimeRegistrationMethod === Method.OpenFormOnDone) {
                    openTimeRegistrationDialog();
                }
                queryClient.invalidateQueries({ queryKey: [TaskQueryKey.WeekPlanner] });
            },
        });
    };
    const onStartStopTimer = async () => {
        const stubMainTask = {
            id: element.parentTaskId,
            templateId: element.parentTemplateId,
            name: element.parentName,
        };
        await startStopTimer(stubMainTask, element);
    };
    const onUpdateDeadline = async (deadline) => {
        const formatted = deadline ? moment(deadline).format('YYYY-MM-DD') : '';
        try {
            const { data: updated, } = await api.base.put(`${config.appUrls.tasks}/api/plannedTasks/Deadline/${element.id}/${formatted}`, null, { params: { taskLevel: element.taskLevel } });
            if (updated === null || updated === void 0 ? void 0 : updated.id) {
                onTasksUpdated([updated]);
            }
            else {
                queryClient.invalidateQueries({ queryKey: [TaskQueryKey.WeekPlanner] });
            }
        }
        catch (err) {
            handleHttpError(intl)(err);
        }
    };
    const getTaskTypeIcon = () => {
        switch (element.type) {
            case TaskType.ActualTask:
                return _jsx("i", { className: "fa fa-user task-type-icon", style: { fontSize: '16px' } });
            case TaskType.DefaultTime:
                return _jsx("i", { className: "fa fa-hourglass task-type-icon" });
            case TaskType.Condition:
                return (_jsx("i", { className: "fa fa-question task-type-icon", title: intl.formatMessage(taskMessages.TimeRegistrationIsNotPossibleTaskTypeCondition) }));
            case TaskType.Mail:
                if (state.status <= 1) {
                    return (_jsx("i", { className: "fa fa-envelope action-icon task-type-icon clickable", onClick: e => {
                            stop(e);
                            openMailDialog();
                        }, title: intl.formatMessage(taskMessages.SendMail) }));
                }
                if (state.status === 2) {
                    return (_jsx("a", Object.assign({ onClick: e => {
                            stop(e);
                            openTaskDetailModal(element);
                        }, title: intl.formatMessage(taskMessages.OpenMailHistory) }, { children: _jsx("i", { className: "fa fa-envelope" }) })));
                }
                return null;
            case TaskType.AutomaticMail:
                if (state.status <= 1) {
                    return (_jsxs("span", Object.assign({ onClick: e => {
                            stop(e);
                            openMailDialog();
                        }, title: intl.formatMessage(taskMessages.AutomaticMailTooltip), className: "fa-stack fa-sm action-icon task-type-icon clickable" }, { children: [_jsx("i", { className: "fa fa-calendar fa-stack-2x text-blue" }), _jsx("i", { className: "fa fa-envelope fa-stack-15x ", style: { left: '2px', top: '5px' } })] })));
                }
                if (state.status === 2) {
                    return (_jsx("a", Object.assign({ onClick: e => {
                            stop(e);
                            openTaskDetailModal(element);
                        }, title: intl.formatMessage(taskMessages.OpenMailHistory) }, { children: _jsx("i", { className: "fa fa-envelope" }) })));
                }
                return null;
            default:
                return null;
        }
    };
    const taskColor = getTaskColor(element.status === TaskStatus.Done ? 0 : element.taskColor || 1);
    const remarkPopover = (_jsxs("span", Object.assign({ id: "popover-trigger-hover-focus" }, { children: [loadingRemark ? _jsx("i", { className: "fa fa-spin fa-spinner" }) : null, (remark === null || remark === void 0 ? void 0 : remark.remark) ? _jsx("span", Object.assign({ style: { whiteSpace: 'pre-line' } }, { children: remark.remark })) : null, (remark === null || remark === void 0 ? void 0 : remark.remark) && remark.scheduleRemark ? (_jsx("hr", { className: "dark-line", style: { marginTop: '10px', marginBottom: '10px' } })) : null, (remark === null || remark === void 0 ? void 0 : remark.scheduleRemark) ? (_jsx("span", Object.assign({ style: { whiteSpace: 'pre-line' } }, { children: remark.scheduleRemark }))) : null] })));
    const documentationPopover = (_jsxs("span", Object.assign({ id: "popover-trigger-hover-focus" }, { children: [loadingDocumentation ? _jsx("i", { className: "fa fa-spin fa-spinner" }) : null, _jsx("div", { 
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML: { __html: sanitizer((documentation === null || documentation === void 0 ? void 0 : documentation.documentation) || '') } })] })));
    const showTimeRegistrationButton = element.type !== TaskType.Condition &&
        (defaultCompany.taskTimeRegistrationMethod !== Method.AutomaticRegistration ||
            (defaultCompany.taskTimeRegistrationMethod === Method.AutomaticRegistration &&
                (state === null || state === void 0 ? void 0 : state.status) === TaskStatus.Done)) &&
        !isTimerRunning;
    const showTimerButton = element.type !== TaskType.Mail &&
        element.type !== TaskType.AutomaticMail &&
        element.type !== TaskType.DefaultTime &&
        (isTimerRunning ||
            ((state === null || state === void 0 ? void 0 : state.status) === TaskStatus.InProgress &&
                defaultCompany.taskTimeRegistrationMethod === Method.AutomaticRegistration));
    const parentLabel = (_c = element.parentName) !== null && _c !== void 0 ? _c : (element.parentTemplateId
        ? formatListValue({
            list: 'tasktemplate',
            id: element.parentTemplateId,
            intl,
            fallback: element.parentTemplateName,
        })
        : null);
    const titleText = (_d = element.name) !== null && _d !== void 0 ? _d : formatListValue({
        list: 'tasktemplate',
        id: element.templateId,
        intl,
        fallback: element.templateName,
    });
    const getDisplayName = (item) => {
        var _a;
        return (_a = item.name) !== null && _a !== void 0 ? _a : formatListValue({
            list: 'tasktemplate',
            id: item.templateId,
            intl,
            fallback: item.templateName,
        });
    };
    // The weekplanner shows a flat list of main tasks and subtasks (no sibling data on `element`
    // itself), so unlike the kanban board we first need to fetch the main task with its children
    // before we can show the full task + documentation. Open the tab synchronously (before the
    // fetch) so browsers don't block it as a popup.
    const openDocumentationInNewTab = () => {
        const documentationWindow = openBlankDocumentationWindow();
        const mainTaskId = element.taskLevel === 1 ? element.parentTaskId : element.id;
        if (!mainTaskId)
            return;
        api.base
            .get(`${config.appUrls.tasks}/api/plannedTasks/details/${mainTaskId}`)
            .then(({ data }) => {
            var _a;
            // Unlike /api/plannedTasks and /api/Overview, this details endpoint's response doesn't
            // reliably carry a hasDocumentation flag (nothing in the task details modal ever reads
            // it either). Rather than silently skip real documentation, just always attempt to fetch
            // it per template id — openTaskDocumentationTab only renders a section when that fetch
            // actually returns something.
            const mainItem = {
                id: data.id,
                templateId: data.templateId,
                hasDocumentation: true,
                name: getDisplayName(data),
            };
            const subItems = ((_a = data.children) !== null && _a !== void 0 ? _a : []).map((child) => ({
                id: child.id,
                templateId: child.templateId,
                hasDocumentation: true,
                name: getDisplayName(child),
            }));
            openTaskDocumentationTab(mainItem, subItems, element.id, documentationWindow);
        });
    };
    const statusToggleValue = {
        id: element.id,
        status: element.status,
        isBlocked: !!element.isBlocked,
        inapplicable: !!element.inapplicable,
        rejected: !!element.rejected,
        hasAttachments: false,
        hasRemark: false,
        taskLevel: element.taskLevel,
        taskType: element.taskType,
        type: element.type,
    };
    const canSelect = selectionMode && !!onToggleSelected && element.status !== TaskStatus.Done;
    // Ctrl/shift+click anywhere on the card selects instead of activating what was clicked.
    const onCardModifierClick = (e) => {
        if (!canSelect || !(e.ctrlKey || e.metaKey || e.shiftKey))
            return;
        // Real links (relation name) keep the browser's ctrl+click open-in-new-tab behavior.
        if (e.target.closest('a[href]'))
            return;
        e.preventDefault();
        onToggleSelected(element, e);
    };
    // Shift is a selection gesture here — suppress the native text selection (and the scroll jump
    // it can cause) before it starts. Capture phase: inner regions stop mousedown propagation.
    const onCardMouseDownCapture = (e) => {
        if (canSelect && e.shiftKey)
            e.preventDefault();
        // react-dates has to know the direction before the calendar opens — it both positions the
        // popup and draws the input's fang from it — and this handler runs before the click that
        // opens it, so the card's current position (grid or panel, scrolled or not) is known. Only a
        // press on a date field decides, so a calendar that is already open is never turned around.
        // Both date fields sit on the same row, so one value covers them.
        const trigger = e.target.closest('.week-planner-item-date');
        if (!trigger)
            return;
        const rect = trigger.getBoundingClientRect();
        const fitsBelow = rect.bottom + CalendarPopupHeight <= window.innerHeight;
        const fitsAbove = rect.top - CalendarPopupHeight >= 0;
        setOpenDatePickerUp(!fitsBelow && fitsAbove);
    };
    return (_jsx("div", Object.assign({ className: "week-planner-item-container", id: `task_${element.id}` }, { children: _jsxs("div", Object.assign({ className: classNames('week-planner-item', {
                'week-planner-item-selected': selectionMode && isSelected,
            }), style: {
                backgroundColor: isDragging
                    ? taskColors.dragging.backgroundColor
                    : taskColor.backgroundColor,
            }, ref: cardRef, onClick: onCardModifierClick, onMouseDownCapture: onCardMouseDownCapture }, { children: [_jsx("div", { className: "dskb-gripper", style: {
                        backgroundColor: (_e = taskColor.color) !== null && _e !== void 0 ? _e : defaultItemType.color,
                    } }), _jsxs("div", Object.assign({ className: "week-planner-item-content" }, { children: [_jsxs("div", Object.assign({ className: "week-planner-item-header" }, { children: [canSelect && (_jsx("span", Object.assign({ onClick: stop, onMouseDown: stop }, { children: _jsx(Checkbox, { value: isSelected, onClick: (newValue, e) => onToggleSelected(element, e), extraClass: "week-planner-item-checkbox" }) }))), _jsxs("a", Object.assign({ id: `element-item-${element.id}`, role: "button", tabIndex: 0, className: classNames('name-property', 'week-planner-item-title', (element.inapplicable || element.rejected) && 'strike'), onClick: e => {
                                        // With a modifier held the card-level handler selects; don't also open the detail.
                                        if (e.ctrlKey || e.metaKey || e.shiftKey)
                                            return;
                                        openTaskDetailModal(element);
                                    } }, { children: [_jsx(TaskIcon, { taskLevel: element.taskLevel }), _jsxs("span", Object.assign({ className: "week-planner-item-title-text" }, { children: [titleText, parentLabel ? _jsxs("span", Object.assign({ className: "text-muted" }, { children: [" (", parentLabel, ")"] })) : null, !!element.executionDuration && (_jsxs("span", { children: [" (", minutesToTimeSpan(element.executionDuration), ")"] }))] }))] })), _jsx("span", Object.assign({ onClick: stop, onMouseDown: stop }, { children: _jsx(TaskStatusToggle, { id: `week-planner-status-${element.id}`, value: statusToggleValue, isMainTask: element.taskLevel !== 1, onTasksUpdated: onTasksUpdated, onOpenDetail: () => openTaskDetailModal(element), onAddRegistration: openTimeRegistrationDialog, showDeleteTask: showDeleteTask, onDeleteTask: onDeleteTask ? () => onDeleteTask(element) : null, pullRight: true }) }))] })), _jsx(RelationName, { relation: element.relation }), _jsxs("div", Object.assign({ className: "week-planner-item-dates" }, { children: [_jsxs("span", Object.assign({ className: "week-planner-item-date week-planner-item-date-execution" }, { children: [_jsx("i", { className: "fa fa-calendar-check-o", title: intl.formatMessage(taskMessages.ExecutionDate) }), _jsx(EditableDatePicker, { value: element.executionDate ? moment.utc(element.executionDate) : null, nullDisplayValue: intl.formatMessage(taskMessages.NoExecutionDate), onChange: date => onUpdateExecutionDate(element, date), readOnly: !canUpdateTask, showTodayButton: true, 
                                            // The card sits in a scrollable column — portal the calendar so it isn't clipped.
                                            appendToBody: true, openDirection: openDatePickerUp ? 'up' : 'down', daySize: CalendarDaySize })] })), _jsxs("span", Object.assign({ className: "week-planner-item-date week-planner-item-date-deadline" }, { children: [_jsx("i", { className: "fa fa-calendar", title: intl.formatMessage(taskMessages.Deadline) }), element.status !== TaskStatus.Done ? (_jsx(EditableDatePicker, { showClearDate: true, value: element.deadline ? moment.utc(element.deadline) : null, nullDisplayValue: intl.formatMessage(taskMessages.NoDeadline), onChange: deadline => onUpdateDeadline(deadline), readOnly: !canUpdateTask, showTodayButton: true, appendToBody: true, openDirection: openDatePickerUp ? 'up' : 'down', daySize: CalendarDaySize })) : element.deadline ? (_jsx(FormattedDateShort, { value: moment.utc(element.deadline).toDate() })) : (_jsx("span", { children: intl.formatMessage(taskMessages.NoDeadline) }))] })), _jsxs("span", Object.assign({ className: "week-planner-item-actions", onClick: stop, onMouseDown: stop }, { children: [element.isBlocked && (_jsx("i", { className: "fa fa-minus-circle text-danger blocked-icon", title: intl.formatMessage(taskMessages.Blocked) })), element.hasAttachments && (_jsx(PopoverStickOnHover, Object.assign({ trigger: ['hover', 'focus'], placement: "top", component: _jsx(TaskAttachmentsPopover, { attachments: attachments, loadingAttachments: loadingAttachments }), shouldUpdatePosition: true, onMouseEnter: () => setLoadAttachments(true), onClick: () => openTaskDetailModal(element) }, { children: _jsx("span", { children: _jsx(PaperclipIcon, {}) }) }))), (element.hasRemark || element.scheduleHasRemark) && (_jsx(PopoverStickOnHover, Object.assign({ trigger: ['hover', 'focus'], placement: "top", component: remarkPopover, shouldUpdatePosition: true, onMouseEnter: () => setLoadRemark(true), onClick: () => openTaskDetailModal(element) }, { children: _jsx("span", { children: _jsx(CommentIcon, {}) }) }))), showTimeRegistrationButton && (_jsx("a", Object.assign({ onClick: e => {
                                                stop(e);
                                                openTimeRegistrationDialog();
                                            } }, { children: _jsx("i", { className: "fa fa-clock-o", title: intl.formatMessage(taskMessages.CreateTimeRegistration) }) }))), showTimerButton && (_jsx("a", Object.assign({ onClick: e => {
                                                stop(e);
                                                onStartStopTimer();
                                            } }, { children: isTimerRunning ? (_jsx("i", { className: "fa fa-pause-circle", title: intl.formatMessage(taskMessages.PauseTimer) })) : (_jsx("i", { className: "fa fa-play-circle", title: intl.formatMessage(taskMessages.StartTimer) })) }))), element.hasDocumentation && (_jsx(PopoverStickOnHover, Object.assign({ trigger: ['hover', 'focus'], placement: (state === null || state === void 0 ? void 0 : state.status) === TaskStatus.Done ? 'left' : 'bottom', component: documentationPopover, shouldUpdatePosition: true, onMouseEnter: () => setLoadDocumentation(true), onClick: openDocumentationInNewTab }, { children: _jsx("i", { className: "fa fa-book clickable" }) }))), getTaskTypeIcon()] }))] }))] }))] })) }), element.id));
};
export default WeekPlannerElementComponent;
